import makeStyles from '@mui/styles/makeStyles';
import { FormattedMessage } from 'react-intl';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import Typography from '@mui/material/Typography';
import MenuItem from '@mui/material/MenuItem/MenuItem';
import { MenuProps, Select, Tooltip } from '@katalon-studio/katalon-ui/v2';
import { Account } from '../../../models';
import { fromAccounts, useAppDispatch } from '../../../store';
import { useNavigate, useQuery } from '../../../routes';
import { ReactComponent as AccountIcon } from '../../icons/AccountIcon.svg';
import { MAX_ACCOUNT_NAME_LENGTH } from '../../../utils/Constants';

const ACCOUNT_MENU_PROPS: Partial<MenuProps> = {
  anchorOrigin: {
    vertical: 'bottom',
    horizontal: 'left',
  },
  transformOrigin: {
    vertical: 'top',
    horizontal: 'left',
  },
  MenuListProps: {
    sx: { paddingTop: '0 !important' },
  },
  PaperProps: {
    sx: {
      maxHeight: 276,
      maxWidth: 400,
      border: '1px solid #e8e8e8',
      padding: '0 12px !important',
    },
  },
  autoFocus: false,
};

const useStyles = makeStyles(theme => ({
  container: {
    alignItems: 'center',
    justifyContent: 'flex-start',
    color: '#fff',
    display: 'inline-flex',
    margin: theme.spacing(0, 1),
  },
  iconContainer: {
    width: theme.spacing(2.5),
    height: theme.spacing(2.5),
    display: 'inline-flex',
    justifyContent: 'center',
    alignItems: 'center',
    color: '#fff',
  },
  icon: {
    width: theme.spacing(2),
    height: theme.spacing(2),
    display: 'inline-flex',
    color: '#fff',
  },
  iconBreadcrumb: {
    width: theme.spacing(1.5),
    height: theme.spacing(1.5),
    marginRight: theme.spacing(1.5),
    display: 'inline-flex',
  },
  nameContainer: {
    marginLeft: theme.spacing(0.5),
    marginRight: theme.spacing(0.5),
    height: theme.spacing(3.5),
    display: 'inline-flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    overflow: 'hidden',
  },
  accountName: {
    fontSize: theme.spacing(1.75),
    fontWeight: 500,
  },
}));

export const AccountSelection = () => {
  const anchorRef = useRef<HTMLButtonElement>(null);
  const classes = useStyles();
  const currentPath = useLocation().pathname;
  const { get } = useQuery();
  const accountId = get('accountId');
  const dispatch = useAppDispatch();
  const { queryDictionary } = useQuery();
  const { replace, replaceQuery } = useNavigate();
  const [selectedAccount, setSelectedAccount] = useState<Account>();
  const userAccounts = useSelector(fromAccounts.selectUserAccounts);

  useEffect(() => {
    if (userAccounts.length > 0) {
      setSelectedAccount(userAccounts.find(account => account.id === Number(accountId)));
    }
  }, [userAccounts, accountId]);

  const handleSelectAccount = (account: Account) => {
    dispatch(fromAccounts.doSetSelectedAccount(account));
    replace(currentPath, replaceQuery({ ...queryDictionary(), accountId: account.id }));
  };

  const renderAccountLabel = (account: Account) => (
    <div className={classes.nameContainer}>
      <Typography className={classes.accountName}>
        <FormattedMessage
          id="layout.header.account.display.name.label"
          values={{
            accountName: account.name.length <= MAX_ACCOUNT_NAME_LENGTH
              ? account.name
              : `${account.name.slice(0, MAX_ACCOUNT_NAME_LENGTH)}...`,
            accountId: account.id,
          }}
        />
      </Typography>
    </div>
  );

  const renderSelectValue = (account: Account) => (
    <div className={classes.container}>
      <div className={classes.iconContainer}>
        <AccountIcon className={classes.icon} />
      </div>
      {renderAccountLabel(account)}
    </div>
  );

  return selectedAccount ? (
    <Select
      ref={anchorRef}
      variant="outlined"
      value={selectedAccount}
      renderValue={() => (selectedAccount.name.length > MAX_ACCOUNT_NAME_LENGTH
        ? (
          <Tooltip
            key={selectedAccount.id}
            title={selectedAccount.name}
            placement="bottom-start"
          >
            {renderSelectValue(selectedAccount)}
          </Tooltip>
        )
        : renderSelectValue(selectedAccount)
      )}
      inputProps={{
        classes: {
          icon: classes.icon,
        },
      }}
      sx={{
        width: 400,
        '.MuiOutlinedInput-notchedOutline': {
          borderColor: 'rgba(228, 219, 233, 0.25)',
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: 'rgba(228, 219, 233, 0.25)',
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: 'rgba(228, 219, 233, 0.25)',
        },
        '.MuiSvgIcon-root ': {
          fill: 'white !important',
        },
      }}
      MenuProps={ACCOUNT_MENU_PROPS}
    >
      {userAccounts.map(account => (
        <MenuItem
          key={account.id}
          onClick={() => handleSelectAccount(account)}
        >
          {
            account.name.length > MAX_ACCOUNT_NAME_LENGTH
              ? (
                <Tooltip
                  key={account.id}
                  title={account.name}
                  placement="bottom-start"
                >
                  {renderAccountLabel(account)}
                </Tooltip>
              )
              : renderAccountLabel(account)
          }
        </MenuItem>
      ))}
    </Select>
  ) : null;
};
